'use client'

import { IconButton, InputAdornment } from '@mui/material'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { useField } from 'formik'
import { useLayoutEffect, useRef, useState } from 'react'

import DefaultTextField from '@/components/global/forms/DefaultTextField'

interface PasswordFieldProps {
  name: string
  label: string
}

export function PasswordField({ name, label }: PasswordFieldProps) {
  const [field, meta] = useField(name)
  const [showPassword, setShowPassword] = useState(false)

  const inputRef = useRef<HTMLInputElement | null>(null)
  const cursorRef = useRef<{ start: number; end: number } | null>(null)

  const handleToggle = () => {
    const input = inputRef.current
    if (!input) return

    cursorRef.current = {
      start: input.selectionStart ?? 0,
      end: input.selectionEnd ?? 0,
    }

    setShowPassword((prev) => !prev)
  }

  useLayoutEffect(() => {
    const input = inputRef.current
    const cursor = cursorRef.current

    if (!input || !cursor) return

    input.focus()
    input.setSelectionRange(cursor.start, cursor.end)

    cursorRef.current = null
  }, [showPassword])

  return (
    <DefaultTextField
      {...field}
      fullWidth
      variant="outlined"
      label={label}
      type={showPassword ? 'text' : 'password'}
      error={meta.touched && Boolean(meta.error)}
      helperText={meta.touched && meta.error}
      inputRef={inputRef}
      slotProps={{
        htmlInput: {
          ref: inputRef,
        },
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={handleToggle}
                edge="end"
                tabIndex={-1}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
      sx={{ maxHeight: 40 }}
    />
  )
}
